import { mainLayoutResolver } from './layouts/main-layout/main-layout.resolver';
import { Routes } from '@angular/router';
import { ERoutes } from './core/enums/routes.enum';
import { authGuard, guestGuard } from './core/guards/auth/auth.guard';
import { subscripedGuard } from './core/guards/subscriped/subscriped.guard';
import { pageTitleResolver } from './core/resolvers/pageTitle/page-title.resolver';
import { AppComponent } from './app.component';
import { appResolver } from './app.resolver';
import { desktopOnlyGuard } from './core/guards/device/desktop-only.guard';
import { mobileOnlyGuard } from './core/guards/device/mobile-only.guard';
import { identityVerificationGuard } from './core/guards/identity-verification/identity-verification.guard';
import { subscriptionFeatureGuard } from './core/guards/subscription-feature/subscription-feature.guard';

const authLayoutRoutes: Routes = [
    {
        path: '',
        loadComponent: () => import('./layouts/auth-layout/auth-layout.component').then(m => m.AuthLayoutComponent),
        canActivateChild: [guestGuard],
        children: [
            {
                path: ERoutes.login,
                loadComponent: () => import('./views/auth/login/login.component').then(m => m.LoginComponent),
                resolve: { meta: pageTitleResolver },
                data: {
                    title: 'Lokinter - Login'
                }
            },
            {
                path: ERoutes.register,
                loadComponent: () => import('./views/auth/register/register.component').then(m => m.RegisterComponent),
                resolve: { meta: pageTitleResolver },
                data: {
                    title: 'Lokinter - Register'
                }
            },
            {
                path: ERoutes.forgotPassword,
                loadComponent: () => import('./views/auth/forgot-password/forgot-password.component').then(m => m.ForgotPasswordComponent),
                resolve: { meta: pageTitleResolver },
                data: {
                    title: 'Lokinter - Forgot Password'
                }
            },
            {
                path: `${ERoutes.resetPassword}`,
                loadComponent: () => import('./views/auth/reset-password/reset-password.component').then(m => m.ResetPasswordComponent),
                resolve: { meta: pageTitleResolver },
                data: {
                    title: 'Lokinter - Reset Password'
                }
            },
            {
                path: ERoutes.welcome,
                loadComponent: () => import('./views/welcome/welcome.component').then(m => m.WelcomeComponent),
                resolve: { meta: pageTitleResolver },
                data: {
                    seoData: 'welcomePage'
                }
            },
        ]
    }
]

const profileLayoutRoutes: Routes = [
    {
        path: ``,
        loadComponent: () => import('./layouts/profile-layout/profile-layout.component').then(m => m.ProfileLayoutComponent),
        canActivateChild: [authGuard],
        children: [
            {
                path: ERoutes.basicInfo,
                loadComponent: () => import('./views/profile/basic-info/basic-info.component').then(m => m.BasicInfoComponent),
                resolve: { meta: pageTitleResolver },
                data: {
                    title: 'Lokinter - Profile Info'
                }
            },
            {
                path: ERoutes.subscription,
                canActivate: [subscriptionFeatureGuard],
                loadComponent: () => import('./views/profile/subscription/subscription.component').then(m => m.SubscriptionComponent),
                resolve: { meta: pageTitleResolver },
                data: {
                    title: 'Lokinter - Subscriptions'
                }
            },
            {
                path: `${ERoutes.checkout}/:id`,
                canActivate: [subscriptionFeatureGuard],
                loadComponent: () => import('./views/profile/checkout/checkout.component').then(m => m.CheckoutComponent),
                resolve: { meta: pageTitleResolver },
                data: {
                    title: 'Lokinter - Checkout'
                }
            },
            {
                path: ERoutes.invoicesQrCodes,
                loadComponent: () => import('./views/profile/qr-codes/qr-codes.component').then(m => m.QrCodesComponent),
                resolve: { meta: pageTitleResolver },
                data: {
                    title: 'Lokinter - QR Codes -  invoices'
                }
            },
            {
                path: ERoutes.walletAccounts,
                loadComponent: () => import('./components/views/profile/top-up/top-up.component').then(m => m.TopUpComponent),
                resolve: { meta: pageTitleResolver },
                data: {
                    title: 'Lokinter - Payout Accounts',
                    walletView: 'accounts'
                }
            },
            {
                path: ERoutes.wallet,
                loadComponent: () => import('./components/views/profile/top-up/top-up.component').then(m => m.TopUpComponent),
                resolve: { meta: pageTitleResolver },
                data: {
                    title: 'Lokinter - My Wallet',
                    walletView: 'wallet'
                }
            },
        ]
    }
]

const mainLayoutRoutes: Routes = [
    {
        path: '',
        loadComponent: () => import('./layouts/main-layout/main-layout.component').then(m => m.MainLayoutComponent),
        canActivateChild: [authGuard],
        resolve: { data: mainLayoutResolver },
        children: [
            {
                path: '',
                redirectTo: `${ERoutes.home}`,
                pathMatch: 'full'
            },
            {
                path: ERoutes.home,
                loadComponent: () => import('./views/home/home.component').then(m => m.HomeComponent),
                resolve: { meta: pageTitleResolver },
                data: {
                    seoData: 'homePage'
                }
            },
            {
                path: ERoutes.home2,
                loadComponent: () => import('./views/home2/home2.component').then(m => m.Home2Component),
                resolve: { meta: pageTitleResolver },
                data: {
                    seoData: 'homePage'
                }
            },
            {
                path: ERoutes.postRequest,
                loadComponent: () => import('./views/post-deliver-request/post-deliver-request.component').then(m => m.PostDeliverRequestComponent),
                canActivate: [identityVerificationGuard],
                resolve: { meta: pageTitleResolver },
                data: {
                    title: 'Lokinter - Post Deliver Request'
                }
            },
            {
                path: ERoutes.postTrip,
                loadComponent: () => import('./views/post-trip/post-trip.component').then(m => m.PostTripComponent),
                canActivate: [identityVerificationGuard, subscripedGuard],
                resolve: { meta: pageTitleResolver },
                data: {
                    title: 'Lokinter - Post Trip'
                }
            },
            {
                path: ERoutes.searchResult,
                loadComponent: () => import('./views/search-result/search-result.component').then(m => m.SearchResultComponent),
                resolve: { meta: pageTitleResolver },
                data: {
                    seoData: 'searchPage'
                }
            },
            {
                path: `${ERoutes.userReviews}/:id`,
                loadComponent: () => import('./views/user-reviews/user-reviews.component').then(m => m.UserReviewsComponent),
                resolve: { meta: pageTitleResolver },
                data: {
                    title: 'Lokinter - User Reviews'
                }
            },
            {
                path: ERoutes.deals,
                canActivate: [desktopOnlyGuard],
                loadComponent: () => import('./views/deals/deals.component').then(m => m.DealsComponent),
                resolve: { meta: pageTitleResolver },
                data: {
                    seoData: 'dealsPage',
                    mobileRedirect: '/m/deals'
                }
            },
            {
                path: ERoutes.chat,
                canActivate: [desktopOnlyGuard],
                loadComponent: () => import('./views/chat/chat.component').then(m => m.ChatComponent),
                resolve: { meta: pageTitleResolver },
                data: {
                    title: 'Lokinter - Chat'
                }
            },
            ...profileLayoutRoutes,
        ]
    }
]

const noLayoutRoutes: Routes = [
    {
        path: '',
        loadComponent: () => import('./layouts/no-layout/no-layout.component').then(m => m.NoLayoutComponent),
        children: [
            {
                path: `${ERoutes.dealDetails}/:refId`,
                loadComponent: () => import('./views/deals/deal-details/deal-details.component').then(m => m.DealDetailsComponent),
                resolve: { meta: pageTitleResolver },
                data: {
                    title: 'Lokinter - Deal Details'
                }
            },
            {
                path: ERoutes.contact,
                loadComponent: () => import('./views/contact-us/contact-us.component').then(m => m.ContactUsComponent),
                resolve: { meta: pageTitleResolver },
                data: {
                    seoData: 'contactPage'
                }
            },
            {
                path: ERoutes.termsConditions,
                loadComponent: () => import('./views/terms-conditions/terms-conditions.component').then(m => m.TermsConditionsComponent),
                resolve: { meta: pageTitleResolver },
                data: {
                    title: 'Lokinter - Terms and Conditions'
                }
            },
        ]
    }
]

// Dedicated mobile layout routes (chat inbox, profile, etc.)
const mobileChatRoutes: Routes = [
    {
        path: 'm',
        loadComponent: () => import('./layouts/mobile-chat-layout/mobile-chat-layout.component').then(m => m.MobileChatLayoutComponent),
        canActivateChild: [mobileOnlyGuard],
        children: [
            {
                path: ERoutes.chat,
                canActivate: [authGuard],
                loadComponent: () => import('./views/chat-mobile-list/chat-mobile-list.component').then(m => m.ChatMobileListComponent),
                resolve: { meta: pageTitleResolver },
                data: { title: 'Lokinter - Inbox (Mobile)' }
            },
            {
                path: `${ERoutes.chat}/:userId`,
                canActivate: [authGuard],
                loadComponent: () => import('./views/chat-mobile/chat-mobile.component').then(m => m.ChatMobileComponent),
                resolve: { meta: pageTitleResolver },
                data: { title: 'Lokinter - Chat (Mobile)' }
            },
            {
                path: ERoutes.basicInfo,
                canActivate: [authGuard],
                loadComponent: () => import('./views/profile/basic-info/basic-info.component').then(m => m.BasicInfoComponent),
                resolve: { meta: pageTitleResolver },
                data: { title: 'Lokinter - Profile Info (Mobile)' }
            },
            {
                path: ERoutes.subscription,
                canActivate: [authGuard, subscriptionFeatureGuard],
                loadComponent: () => import('./views/profile/subscription/subscription-mobile.component').then(m => m.SubscriptionMobileComponent),
                resolve: { meta: pageTitleResolver },
                data: { title: 'Lokinter - Subscription (Mobile)' }
            },
            {
                path: ERoutes.deals,
                canActivate: [authGuard],
                loadComponent: () => import('./views/deals-mobile/deals-mobile.component').then(m => m.DealsMobileComponent),
                resolve: { meta: pageTitleResolver },
                data: { title: 'Lokinter - Deals (Mobile)' }
            },
        ]
    }
]






export const routes: Routes = [
    {
        path: '',
        resolve: { data: appResolver },
        children: [
            ...mainLayoutRoutes,
            ...authLayoutRoutes,
            ...noLayoutRoutes,
            ...mobileChatRoutes,
        ]
    }
];
